import React, { useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import AppIcon from '@/components/AppIcon';

interface SelectOption {
  value: string;
  label: string;
}

interface ActiveFilters {
  dateRange: string;
  updateType: string;
  searchQuery: string;
}

interface UpdateFiltersProps {
  onFilterChange: (filters: any) => void;
  onSearch: (query: string) => void;
}

const UpdateFilters: React.FC<UpdateFiltersProps> = ({ onFilterChange, onSearch }) => {
  const { t } = useTranslation();
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    dateRange: '',
    updateType: '',
    searchQuery: ''
  });

  const dateRangeOptions: SelectOption[] = [
    { value: '', label: t('performance.teamHub.allTime') },
    { value: 'today', label: t('performance.teamHub.today') },
    { value: 'week', label: t('performance.teamHub.thisWeek') },
    { value: 'month', label: t('performance.teamHub.thisMonth') },
    { value: 'quarter', label: t('performance.teamHub.thisQuarter') }
  ];

  const updateTypeOptions: SelectOption[] = [
    { value: '', label: t('performance.teamHub.allTypes') },
    { value: 'team-achievement', label: t('performance.teamHub.typeTeamAchievement') },
    { value: 'progress-milestone', label: t('performance.teamHub.typeProgressMilestone') },
    { value: 'goal-update', label: t('performance.teamHub.typeGoalUpdate') },
    { value: 'recognition-highlight', label: t('performance.teamHub.typeRecognitionHighlight') },
    { value: 'general-update', label: t('performance.teamHub.typeGeneralUpdate') },
    { value: 'announcement', label: t('performance.teamHub.typeAnnouncement') },
    { value: 'question', label: t('performance.teamHub.typeQuestion') },
    { value: 'milestone', label: t('performance.teamHub.typeMilestone') }
  ];

  const handleFilterChange = (key: keyof ActiveFilters, value: string) => {
    const newFilters = { ...activeFilters, [key]: value };
    setActiveFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearch = (query: string) => {
    const newFilters = { ...activeFilters, searchQuery: query };
    setActiveFilters(newFilters);
    onSearch(query);
  };

  const clearAllFilters = () => {
    const clearedFilters: ActiveFilters = {
      dateRange: '',
      updateType: '',
      searchQuery: ''
    };
    setActiveFilters(clearedFilters);
    onFilterChange(clearedFilters);
    onSearch('');
  };

  const getActiveFilterCount = (): number => {
    return Object.values(activeFilters).filter(value => value && value !== '').length;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-3 mb-3 w-full">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AppIcon name="SlidersHorizontal" size={20} className="text-muted-foreground" />
          <h3 className="text-lg font-medium text-foreground">
            {t('performance.teamHub.filterUpdates')}
          </h3>
          {getActiveFilterCount() > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-primary text-primary">
              {getActiveFilterCount()} {t('performance.teamHub.active')}
            </span>
          )}
        </div>
        {getActiveFilterCount() > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllFilters}
          >
            <AppIcon name="X" size={14} className="me-1" />
            {t('performance.teamHub.clearAll')}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="relative">
          <AppIcon
            name="Search"
            className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            size={16}
          />
          <input
            className="w-full ps-10 pe-10 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            type="text"
            placeholder={t('performance.teamHub.searchPlaceholder')}
            value={activeFilters.searchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
          />
          {activeFilters.searchQuery && (
            <button
              className="absolute end-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md hover:bg-muted text-muted-foreground"
              onClick={() => handleSearch('')}
            >
              <AppIcon name="X" size={14} />
            </button>
          )}
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <Select
              options={dateRangeOptions}
              value={activeFilters.dateRange}
              onChange={(val: string) => handleFilterChange('dateRange', val)}
              placeholder={t('performance.teamHub.dateRangePlaceholder')}
            />
          </div>
          <div className="flex-1">
            <Select
              options={updateTypeOptions}
              value={activeFilters.updateType}
              onChange={(val: string) => handleFilterChange('updateType', val)}
              placeholder={t('performance.teamHub.updateTypePlaceholder')}
            />
          </div>
        </div>

        {/* Active Filters Display */}
        {getActiveFilterCount() > 0 && (
          <div className="pt-2 border-t border-border">
            <p className="text-sm text-muted-foreground mb-1">
              {t('performance.teamHub.activeFilters')}:
            </p>
            <div className="flex flex-wrap gap-1">
              {activeFilters.dateRange && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {t('performance.teamHub.dateLabel')}: {dateRangeOptions.find(opt => opt.value === activeFilters.dateRange)?.label}
                  <button
                    className="ms-0.5 hover:text-primary/80"
                    onClick={() => handleFilterChange('dateRange', '')}
                  >
                    <AppIcon name="X" size={12} />
                  </button>
                </span>
              )}
              {activeFilters.updateType && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {t('performance.teamHub.typeLabel')}: {updateTypeOptions.find(opt => opt.value === activeFilters.updateType)?.label}
                  <button
                    className="ms-0.5 hover:text-primary/80"
                    onClick={() => handleFilterChange('updateType', '')}
                  >
                    <AppIcon name="X" size={12} />
                  </button>
                </span>
              )}
              {activeFilters.searchQuery && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {t('performance.teamHub.searchLabel')}: &quot;{activeFilters.searchQuery}&quot;
                  <button
                    className="ms-0.5 hover:text-primary/80"
                    onClick={() => handleSearch('')}
                  >
                    <AppIcon name="X" size={12} />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateFilters;
